import Card from 'components/card';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { MdEuro, MdShoppingCart, MdTrendingUp } from 'react-icons/md';
import { getBiDailyRevenue } from 'interfaces/bi';
import { formatCurrency, formatNumber } from './helpers';

/** Small inline stat tile. */
function Stat(props: { label: string; value: string; icon: JSX.Element; sub?: string }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-lightPrimary text-2xl text-toola-500">
                {props.icon}
            </div>
            <div>
                <p className="text-xs font-medium text-gray-600">{props.label}</p>
                <h3 className="text-lg font-bold text-navy-700">{props.value}</h3>
                {props.sub && <p className="text-[11px] text-gray-400">{props.sub}</p>}
            </div>
        </div>
    );
}

/**
 * KPI #1 — Daily revenue ("CA quotidien").
 *
 * Renders an area chart of the per-day revenue over the selected period, plus
 * headline stats for the period total and today's figure. Reads from the
 * pre-aggregated `/bi/revenue/daily` endpoint.
 */
function RevenueChart(props: { from: string; to: string }) {
    const { from, to } = props;
    const [rows, setRows] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            // The daily endpoint is capped at 366 rows server-side; one page covers any range here.
            const res = await getBiDailyRevenue({ from, to, page: 1, limit: 366 });
            if (!active) return;
            setRows(res?.data ?? []);
            setSummary(res?.summary ?? null);
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, [from, to]);

    const series = useMemo(
        () => [
            {
                name: 'CA',
                data: rows.map((r) => [new Date(r.day).getTime(), Number(r.revenue) || 0]),
            },
        ],
        [rows],
    );

    const options: any = {
        chart: {
            type: 'area',
            toolbar: { show: false },
            dropShadow: {
                enabled: true,
                top: 13,
                left: 0,
                blur: 10,
                opacity: 0.1,
                color: '#0260CB',
            },
        },
        colors: ['#0260CB'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] },
        },
        xaxis: {
            type: 'datetime',
            labels: { style: { colors: '#A3AED0', fontSize: '10px' } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: '#A3AED0', fontSize: '10px' },
                formatter: (v: number) => formatNumber(v),
            },
        },
        tooltip: {
            theme: 'dark',
            x: { format: 'dd MMM yyyy' },
            y: { formatter: (v: number) => formatCurrency(v) },
        },
        grid: { show: false },
    };

    return (
        <Card extra="p-5 border h-full">
            <div className="mb-3 flex items-start justify-between">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">Chiffre d'affaires</h4>
                    <p className="text-sm text-gray-500">CA quotidien sur la période</p>
                </div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Stat
                    label="CA du jour"
                    value={formatCurrency(summary?.todayRevenue)}
                    icon={<MdEuro />}
                    sub={`${formatNumber(summary?.todayOrdersCount)} commande(s)`}
                />
                <Stat
                    label="CA de la période"
                    value={formatCurrency(summary?.revenue)}
                    icon={<MdTrendingUp />}
                />
                <Stat
                    label="Commandes période"
                    value={formatNumber(summary?.ordersCount)}
                    icon={<MdShoppingCart />}
                />
            </div>

            {loading ? (
                <div className="flex h-[260px] items-center justify-center">
                    <p className="text-gray-400">Chargement...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-[260px] items-center justify-center">
                    <p className="text-gray-400">Aucune donnée sur la période.</p>
                </div>
            ) : (
                <ReactApexChart options={options} series={series} type="area" height={260} />
            )}
        </Card>
    );
}

export default RevenueChart;
